// Cinematic Design System - Animation tokens and visual constants

export const CINEMATIC_COLORS = {
    // Primary palette - Rich's brand red for key elements
    primary: {
        50: '#fef2f2',
        100: '#fee2e2',
        200: '#fecaca',
        300: '#fca5a5',
        400: '#f87171',
        500: '#ef4444',
        600: '#D31145',  // Rich's Brand Red - Updated to user's HEX
        700: '#b91c1c',
        800: '#991b1b',
        900: '#7f1d1d'
    },

    // Secondary palette - Warm grays for professional feel
    secondary: {
        50: '#f9fafb',
        100: '#f3f4f6',
        200: '#e5e7eb',
        300: '#d1d5db',
        400: '#9ca3af',
        500: '#6b7280',
        600: '#4b5563',
        700: '#374151',
        800: '#1f2937',
        900: '#111827'
    },

    // Accent palette - Subtle blues for contrast
    accent: {
        50: '#eff6ff',
        100: '#dbeafe',
        200: '#bfdbfe',
        300: '#93c5fd',
        400: '#60a5fa',
        500: '#3b82f6',
        600: '#2563eb',
        700: '#1d4ed8',
        800: '#1e40af',
        900: '#1e3a8a'
    },

    // Warning palette - Professional yellow for warnings
    warning: {
        50: '#fffbeb',
        100: '#fef3c7',
        200: '#fde68a',
        300: '#fcd34d',
        400: '#fbbf24',
        500: '#f59e0b',
        600: '#d97706',
        700: '#b45309',
        800: '#92400e',
        900: '#78350f'
    },

    // Danger palette - Rich's brand red variations for critical issues
    danger: {
        50: '#fef2f2',
        100: '#fee2e2',
        200: '#fecaca',
        300: '#fca5a5',
        400: '#f87171',
        500: '#ef4444',
        600: '#dc2626',
        700: '#b91c1c',
        800: '#991b1b',
        900: '#7f1d1d'
    },

    // Neutral palette - rich grays with subtle warm undertone
    neutral: {
        50: '#fafaf9',
        100: '#f5f5f4',
        200: '#e7e5e4',
        300: '#d6d3d1',
        400: '#a8a29e',
        500: '#78716c',
        600: '#57534e',
        700: '#44403c',
        800: '#292524',
        900: '#1c1917'
    },

    // Glass effects and overlays
    glass: {
        light: 'rgba(255, 255, 255, 0.1)',
        medium: 'rgba(255, 255, 255, 0.2)',
        heavy: 'rgba(255, 255, 255, 0.3)',
        backdrop: 'rgba(0, 0, 0, 0.4)'
    }
} as const;

// Semantic Color System - Maps intent to values (Updated for white background)
export const SEMANTIC_COLORS = {
    bg: {
        base: '#ffffff',      // White background
        alt: '#f9fafb',       // Light gray alternative surface
        subtle: 'rgba(0, 0, 0, 0.02)'  // Very subtle overlay
    },
    surface: {
        raised: 'rgba(0, 0, 0, 0.03)',    // Cards, modals
        overlay: 'rgba(0, 0, 0, 0.25)',   // Backdrop overlays
        glass: 'rgba(255, 255, 255, 0.8)', // Glass morphism effect
        border: 'rgba(0, 0, 0, 0.08)'     // Subtle borders
    },
    text: {
        primary: '#111827',                // Dark text on white
        secondary: 'rgba(17, 24, 39, 0.75)', // Secondary dark text
        tertiary: 'rgba(17, 24, 39, 0.55)',  // Tertiary/muted text
        inverse: '#ffffff'                 // White text on dark backgrounds
    },
    status: {
        positive: '#16a34a',  // Success, high scores (darker green)
        caution: '#d97706',   // Warning, medium scores (darker yellow)
        risk: '#D31145',      // Danger, low scores (Rich's brand red - Updated)
        neutral: '#2563eb',   // Information, neutral state (darker blue)
        info: '#0ea5e9'       // General information
    },
    focus: {
        ring: '#D31145',                      // Focus outline color (Rich's brand - Updated)
        ringGlow: 'rgba(211, 17, 69, 0.35)'  // Focus glow effect - Updated RGBA
    }
} as const;

// Gauge-specific semantic colors (Updated for Rich's brand)
export const GAUGE_COLORS = {
    score: {
        low: '#D31145',       // 0-50: Rich's Brand Red - Updated
        mid: '#d97706',       // 50-75: Professional amber
        high: '#16a34a'       // 75-100: Professional green
    },
    track: 'rgba(0, 0, 0, 0.08)',  // Light gray background track
    segments: {
        critical: '#D31145',  // Critical issues (Rich's brand red - Updated)
        warning: '#d97706',   // Warning issues (professional amber)
        good: '#16a34a'       // Good state (professional green)
    }
} as const;

// Palette variants for different use cases
export type CinematicVariant = 'vibrant' | 'calm' | 'highContrast';

// Base semantic color structure
type SemanticColorStructure = {
    bg: {
        base: string;
        alt: string;
        subtle: string;
    };
    surface: {
        raised: string;
        overlay: string;
        glass: string;
        border: string;
    };
    text: {
        primary: string;
        secondary: string;
        tertiary: string;
        inverse: string;
    };
    status: {
        positive: string;
        caution: string;
        risk: string;
        neutral: string;
        info: string;
    };
    focus: {
        ring: string;
        ringGlow: string;
    };
};

export const PALETTE_VARIANTS: Record<CinematicVariant, {
    name: string;
    description: string;
    colors: SemanticColorStructure;
    intensity: {
        glow: number;        // 0-1 glow intensity multiplier
        saturation: number;  // 0-1 saturation multiplier
        contrast: number;    // Contrast ratio target
    };
}> = {
    vibrant: {
        name: 'Vibrant',
        description: 'Dynamic and engaging with vivid colors',
        colors: SEMANTIC_COLORS,
        intensity: { glow: 1, saturation: 1, contrast: 4.5 }
    },
    calm: {
        name: 'Calm',
        description: 'Analytical mode with softer, desaturated tones',
        colors: {
            ...SEMANTIC_COLORS,
            status: {
                positive: '#14b8a6',  // Teal instead of green
                caution: '#d97706',   // Muted orange
                risk: '#dc2626',      // Slightly muted red
                neutral: '#0891b2',   // Cyan instead of blue
                info: '#0284c7'
            }
        },
        intensity: { glow: 0.3, saturation: 0.7, contrast: 4.5 }
    },
    highContrast: {
        name: 'High Contrast',
        description: 'Maximum accessibility with enhanced contrast',
        colors: {
            ...SEMANTIC_COLORS,
            bg: {
                base: '#000000',
                alt: '#0a0a0a',
                subtle: 'rgba(255, 255, 255, 0.05)'
            },
            text: {
                primary: '#ffffff',
                secondary: 'rgba(255, 255, 255, 0.9)',
                tertiary: 'rgba(255, 255, 255, 0.8)',
                inverse: '#000000'
            },
            surface: {
                raised: 'rgba(255, 255, 255, 0.1)',
                overlay: 'rgba(0, 0, 0, 0.8)',
                glass: 'rgba(255, 255, 255, 0.15)',
                border: 'rgba(255, 255, 255, 0.3)'
            }
        },
        intensity: { glow: 0, saturation: 1, contrast: 7 }
    }
} as const;

export const CINEMATIC_GRADIENTS = {
    hero: 'linear-gradient(135deg, #D31145 0%, #991b1b 100%)',
    heroOverlay: 'linear-gradient(135deg, rgba(211, 17, 69, 0.1) 0%, rgba(153, 27, 27, 0.1) 100%)',

    dimensions: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
    dimensionsOverlay: 'linear-gradient(135deg, rgba(243, 244, 246, 0.8) 0%, rgba(229, 231, 235, 0.8) 100%)',

    problems: 'linear-gradient(135deg, #D31145 0%, #991b1b 50%, #D31145 100%)',
    problemsOverlay: 'linear-gradient(135deg, rgba(211,17,69,0.1) 0%, rgba(153,27,27,0.08) 50%, rgba(211,17,69,0.1) 100%)',

    prescriptions: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 50%, #e5e7eb 100%)',
    prescriptionsOverlay: 'linear-gradient(135deg, rgba(249,250,251,0.9) 0%, rgba(243,244,246,0.9) 50%, rgba(229,231,235,0.9) 100%)',

    heatmap: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
    heatmapOverlay: 'linear-gradient(135deg, rgba(219, 234, 254, 0.8) 0%, rgba(191, 219, 254, 0.8) 100%)',

    patterns: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
    patternsOverlay: 'linear-gradient(135deg, rgba(220, 252, 231, 0.8) 0%, rgba(187, 247, 208, 0.8) 100%)',

    plan: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
    planOverlay: 'linear-gradient(135deg, rgba(254, 243, 199, 0.8) 0%, rgba(253, 230, 138, 0.8) 100%)',

    card: 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 251, 0.9) 100%)',
    cardHover: 'linear-gradient(145deg, rgba(255, 255, 255, 1) 0%, rgba(243, 244, 246, 0.95) 100%)'
} as const;

export const ANIMATION_TOKENS = {
    // Durations
    duration: {
        instant: '0ms',
        fast: '120ms',
        medium: '240ms',
        slow: '340ms',
        slower: '500ms',
        slowest: '800ms',
        countUp: '600ms',
        pageTransition: '1200ms'
    },

    // Easing curves
    easing: {
        easeInOut: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
        easeOut: 'cubic-bezier(0.33, 1, 0.68, 1)',
        easeIn: 'cubic-bezier(0.32, 0, 0.67, 0)',
        bounceOut: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
        spring: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)'
    },

    // Stagger delays
    stagger: {
        fast: '60ms',
        medium: '100ms',
        slow: '150ms'
    },

    // Parallax settings
    parallax: {
        foreground: 1,
        midground: 0.95,
        background: 0.9,
        maxOffset: 20,
        maxOffsetMobile: 8
    },

    // Scale bounds
    scale: {
        subtle: 1.02,
        gentle: 1.05,
        noScale: 1
    },

    // Z-index layers
    zIndex: {
        stickyMeta: 40,
        drawer: 50,
        modal: 60,
        tooltip: 70
    }
} as const;

export const CINEMATIC_SHADOWS = {
    card: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    cardHover: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    drawer: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    glow: '0 0 20px rgba(102, 126, 234, 0.3)'
} as const;

export const TYPOGRAPHY_TOKENS = {
    hero: {
        fontSize: 'clamp(2.5rem, 5vw, 4rem)',
        fontWeight: '700',
        lineHeight: '1.1',
        letterSpacing: '-0.02em'
    },

    sectionTitle: {
        fontSize: 'clamp(1.875rem, 3vw, 2.5rem)',
        fontWeight: '600',
        lineHeight: '1.2',
        letterSpacing: '-0.01em'
    },

    cardTitle: {
        fontSize: '1.25rem',
        fontWeight: '600',
        lineHeight: '1.3'
    },

    metric: {
        fontSize: 'clamp(2rem, 4vw, 3rem)',
        fontWeight: '700',
        lineHeight: '1',
        fontFeatureSettings: '"tnum"' // Tabular figures
    }
} as const;

// Motion tier system for progressive animation control
export const MOTION_TIERS = {
    essential: 0,    // Always on (basic fade for readability)
    narrative: 1,    // Entrance animations (disabled in reduced motion)
    ambient: 2,      // Subtle glows, floating (disabled in calm/reduced motion)
    celebratory: 3   // Completion effects (only in vibrant + not reduced motion)
} as const;

export type MotionTier = typeof MOTION_TIERS[keyof typeof MOTION_TIERS];

// Enhanced CSS Custom Properties Generator with variant support
export function generateCinematicCSS(variant: CinematicVariant = 'vibrant'): string {
    const variantData = PALETTE_VARIANTS[variant];
    const { colors, intensity } = variantData;

    return `
:root {
  /* Animation tokens */
  --anim-duration-fast: ${ANIMATION_TOKENS.duration.fast};
  --anim-duration-medium: ${ANIMATION_TOKENS.duration.medium};
  --anim-duration-slow: ${ANIMATION_TOKENS.duration.slow};
  --anim-duration-slower: ${ANIMATION_TOKENS.duration.slower};
  --anim-duration-count-up: ${ANIMATION_TOKENS.duration.countUp};
  --anim-duration-page-transition: ${ANIMATION_TOKENS.duration.pageTransition};
  
  --anim-ease-in-out: ${ANIMATION_TOKENS.easing.easeInOut};
  --anim-ease-out: ${ANIMATION_TOKENS.easing.easeOut};
  --anim-ease-in: ${ANIMATION_TOKENS.easing.easeIn};
  --anim-ease-bounce: ${ANIMATION_TOKENS.easing.bounceOut};
  --anim-ease-spring: ${ANIMATION_TOKENS.easing.spring};
  
  --stagger-fast: ${ANIMATION_TOKENS.stagger.fast};
  --stagger-medium: ${ANIMATION_TOKENS.stagger.medium};
  --stagger-slow: ${ANIMATION_TOKENS.stagger.slow};
  
  --parallax-max: ${ANIMATION_TOKENS.parallax.maxOffset}px;
  --parallax-mobile: ${ANIMATION_TOKENS.parallax.maxOffsetMobile}px;
  --scale-subtle: ${ANIMATION_TOKENS.scale.subtle};
  --scale-gentle: ${ANIMATION_TOKENS.scale.gentle};

  /* Semantic Background Colors */
  --color-bg-base: ${colors.bg.base};
  --color-bg-alt: ${colors.bg.alt};
  --color-bg-subtle: ${colors.bg.subtle};

  /* Surface Colors */
  --color-surface-raised: ${colors.surface.raised};
  --color-surface-overlay: ${colors.surface.overlay};
  --color-surface-glass: ${colors.surface.glass};
  --color-surface-border: ${colors.surface.border};

  /* Text Colors */
  --color-text-primary: ${colors.text.primary};
  --color-text-secondary: ${colors.text.secondary};
  --color-text-tertiary: ${colors.text.tertiary};
  --color-text-inverse: ${colors.text.inverse};

  /* Status Colors */
  --color-status-positive: ${colors.status.positive};
  --color-status-caution: ${colors.status.caution};
  --color-status-risk: ${colors.status.risk};
  --color-status-neutral: ${colors.status.neutral};
  --color-status-info: ${colors.status.info};

  /* Focus Colors */
  --color-focus-ring: ${colors.focus.ring};
  --color-focus-ring-glow: ${colors.focus.ringGlow};

  /* Gauge Colors */
  --gauge-score-low: ${GAUGE_COLORS.score.low};
  --gauge-score-mid: ${GAUGE_COLORS.score.mid};
  --gauge-score-high: ${GAUGE_COLORS.score.high};
  --gauge-track: ${GAUGE_COLORS.track};
  --gauge-segment-critical: ${GAUGE_COLORS.segments.critical};
  --gauge-segment-warning: ${GAUGE_COLORS.segments.warning};
  --gauge-segment-good: ${GAUGE_COLORS.segments.good};
  
  /* Legacy color compatibility (for gradual migration) */
  --color-primary-500: ${CINEMATIC_COLORS.primary[500]};
  --color-primary-600: ${CINEMATIC_COLORS.primary[600]};
  --color-secondary-500: ${CINEMATIC_COLORS.secondary[500]};
  --color-accent-500: ${CINEMATIC_COLORS.accent[500]};
  --color-warning-500: ${CINEMATIC_COLORS.warning[500]};
  --color-danger-500: ${CINEMATIC_COLORS.danger[500]};

  /* Enhanced Gradients */
  --gradient-hero: ${CINEMATIC_GRADIENTS.hero};
  --gradient-hero-overlay: ${CINEMATIC_GRADIENTS.heroOverlay};
  --gradient-card: ${CINEMATIC_GRADIENTS.card};
  --gradient-card-hover: ${CINEMATIC_GRADIENTS.cardHover};
  --gradient-dimensions: ${CINEMATIC_GRADIENTS.dimensions};
  
  /* Shadows with intensity multiplier */
  --shadow-card: ${CINEMATIC_SHADOWS.card};
  --shadow-card-hover: ${CINEMATIC_SHADOWS.cardHover};
  --shadow-glow: ${CINEMATIC_SHADOWS.glow};
  --shadow-drawer: ${CINEMATIC_SHADOWS.drawer};

  /* Focus Ring Utility */
  --focus-ring: 2px solid var(--color-focus-ring);
  --focus-ring-offset: 2px;
  --focus-ring-glow: 0 0 0 4px var(--color-focus-ring-glow);

  /* Motion Intensity Controls */
  --motion-glow-intensity: ${intensity.glow};
  --motion-saturation: ${intensity.saturation};
  
  /* Z-Index System */
  --z-sticky-meta: ${ANIMATION_TOKENS.zIndex.stickyMeta};
  --z-drawer: ${ANIMATION_TOKENS.zIndex.drawer};
  --z-modal: ${ANIMATION_TOKENS.zIndex.modal};
  --z-tooltip: ${ANIMATION_TOKENS.zIndex.tooltip};
}

/* Responsive adjustments */
@media (max-width: 768px) {
  :root {
    --parallax-max: var(--parallax-mobile);
    --scale-subtle: 1; /* Reduce scaling on mobile */
  }
}

/* Reduced motion preferences */
@media (prefers-reduced-motion: reduce) {
  :root {
    --anim-duration-fast: 0ms;
    --anim-duration-medium: 0ms;
    --anim-duration-slow: 0ms;
    --anim-duration-slower: 0ms;
    --anim-duration-count-up: 0ms;
    --anim-duration-page-transition: 0ms;
    --parallax-max: 0px;
    --scale-subtle: 1;
    --scale-gentle: 1;
    --motion-glow-intensity: 0;
  }
  
  .cinematic-parallax { transform: none !important; }
  .cinematic-reveal { transition: none !important; }
  .cinematic-count-up { transition: none !important; }
  .cinematic-glow { box-shadow: none !important; }
}

/* High contrast mode enhancements */
@media (prefers-contrast: high) {
  :root {
    --color-surface-border: rgba(255, 255, 255, 0.4);
    --motion-glow-intensity: 0;
  }
}

/* Focus management utilities */
.focus-visible {
  outline: var(--focus-ring);
  outline-offset: var(--focus-ring-offset);
  box-shadow: var(--focus-ring-glow);
}

/* Motion tier classes for conditional animations */
.motion-tier-0 { /* Essential - always animate */ }
.motion-tier-1 { /* Narrative - disabled in reduced motion */ }
.motion-tier-2 { /* Ambient - disabled in reduced motion or calm variant */ }
.motion-tier-3 { /* Celebratory - only in vibrant + motion enabled */ }

@media (prefers-reduced-motion: reduce) {
  .motion-tier-1,
  .motion-tier-2,
  .motion-tier-3 {
    animation: none !important;
    transition: none !important;
    transform: none !important;
  }
}

/* Variant-specific motion suppression for calm mode */
[data-cinematic-variant="calm"] .motion-tier-2,
[data-cinematic-variant="calm"] .motion-tier-3,
[data-cinematic-variant="highContrast"] .motion-tier-2,
[data-cinematic-variant="highContrast"] .motion-tier-3 {
  animation: none !important;
  box-shadow: none !important;
  filter: none !important;
}`;
}